import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Timer, FolderOpen, PlusCircle, X, User, Calendar, Clock, MapPin, Sparkles } from 'lucide-react';
import { Language, CompleteKundali } from '../types/kundali';

interface BtrPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  lang: Language;
  currentKundali?: CompleteKundali;
  onSelectCurrent: () => void;
  onSelectExisting: () => void;
  onSelectNew: () => void;
}

export const BtrPromptModal: React.FC<BtrPromptModalProps> = ({
  isOpen,
  onClose,
  lang,
  currentKundali,
  onSelectCurrent,
  onSelectExisting,
  onSelectNew
}) => {
  const isHi = lang === 'hi';

  if (!isOpen) return null;

  const details = currentKundali?.birthDetails;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs select-none">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          className="bg-astro-card border border-astro-border rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-astro-border bg-astro-secondary-surface/50">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-red-100 flex items-center justify-center text-[#DC2626]">
                <Timer className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-serif font-bold text-base text-astro-text">
                  {isHi ? 'जन्म समय शुद्धि (BTR Studio)' : 'Birth Time Rectification'}
                </h3>
                <p className="text-xs text-astro-secondary-text">
                  {isHi ? 'प्रोफ़ाइल का चयन करें' : 'Select profile source for rectification'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-astro-secondary-text hover:text-astro-text p-1.5 rounded-lg hover:bg-astro-border/50 transition cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            <p className="text-xs text-astro-text/80 leading-relaxed">
              {isHi
                ? 'अपनी वर्तमान सक्रिय कुंडली के साथ BTR शुरू करें, सहेजे गए वॉल्ट से चुनें, या नया प्रोफ़ाइल बनाएं।'
                : 'Rectify your current active chart, pick an existing profile from your Vault, or create a new birth profile.'}
            </p>

            {/* Current Active Kundali Card */}
            {details && (
              <div
                onClick={onSelectCurrent}
                className="p-3.5 bg-gradient-to-r from-[#FAF6ED] to-amber-50/50 border border-astro-primary/30 hover:border-astro-primary rounded-xl transition-all cursor-pointer group space-y-2 shadow-2xs"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-astro-primary text-astro-bg flex items-center justify-center font-bold text-xs">
                      <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold text-astro-primary uppercase tracking-wider block">
                        {isHi ? 'वर्तमान सक्रिय कुंडली' : 'Current Active Profile'}
                      </span>
                      <h4 className="font-bold text-sm text-astro-text group-hover:text-astro-primary transition-colors">
                        {details.name}
                      </h4>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 bg-astro-primary text-astro-bg text-xs font-bold rounded-lg group-hover:scale-105 transition-transform">
                    {isHi ? 'BTR शुरू करें →' : 'Rectify Now →'}
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-3 text-[11px] text-astro-secondary-text pt-1 border-t border-astro-border/60">
                  <span className="flex items-center gap-1 font-mono">
                    <Calendar className="w-3 h-3 text-astro-primary" /> {details.date}
                  </span>
                  <span className="flex items-center gap-1 font-mono">
                    <Clock className="w-3 h-3 text-astro-primary" /> {details.time}
                  </span>
                  <span className="flex items-center gap-1 truncate max-w-[200px]">
                    <MapPin className="w-3 h-3 text-astro-primary shrink-0" /> {details.placeName}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-1">
              <button
                onClick={onSelectExisting}
                className="flex flex-col items-center justify-center p-3.5 rounded-xl border border-astro-border bg-astro-bg hover:bg-orange-50 hover:border-orange-200 transition-all group cursor-pointer text-center"
              >
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 mb-1.5 group-hover:scale-110 transition-transform">
                  <FolderOpen className="w-4 h-4" />
                </div>
                <span className="font-semibold text-xs text-astro-text group-hover:text-orange-900">
                  {isHi ? 'कुंडली वॉल्ट' : 'Kundali Vault'}
                </span>
                <span className="text-[10px] text-astro-secondary-text">
                  {isHi ? 'सहेजी गई कुंडलियाँ' : 'Pick from vault'}
                </span>
              </button>

              <button
                onClick={onSelectNew}
                className="flex flex-col items-center justify-center p-3.5 rounded-xl border border-astro-border bg-astro-bg hover:bg-emerald-50 hover:border-emerald-200 transition-all group cursor-pointer text-center"
              >
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 mb-1.5 group-hover:scale-110 transition-transform">
                  <PlusCircle className="w-4 h-4" />
                </div>
                <span className="font-semibold text-xs text-astro-text group-hover:text-emerald-900">
                  {isHi ? 'नया प्रोफ़ाइल' : 'New Profile'}
                </span>
                <span className="text-[10px] text-astro-secondary-text">
                  {isHi ? 'नई जन्म विवरणी' : 'Enter fresh details'}
                </span>
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-3 bg-astro-secondary-surface/50 border-t border-astro-border flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-astro-secondary-text hover:text-astro-text transition cursor-pointer"
            >
              {isHi ? 'रद्द करें' : 'Cancel'}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
